import { supabase } from '../lib/supabase';

export const projectService = {
  async getAllProjects() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    // Get user profile to check role
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    
    let query = supabase?.from('projects')?.select(`
        *,
        client:user_profiles!projects_client_id_fkey(id, email, full_name),
        creator:user_profiles!projects_created_by_fkey(id, email, full_name)
      `);

    // If user is cliente (not admin), filter to only their assigned projects
    if (profile?.role === 'cliente') {
      query = query?.eq('client_id', user?.id);
    }
    // Admins see all projects (no filter applied)

    const { data, error } = await query?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(project => ({
      id: project?.id,
      name: project?.name,
      description: project?.description,
      clientId: project?.client_id,
      clientName: project?.client?.full_name || 'Não atribuído',
      clientEmail: project?.client?.email,
      createdBy: project?.created_by,
      creatorName: project?.creator?.full_name,
      startDate: project?.start_date,
      estimatedEndDate: project?.estimated_end_date,
      status: project?.status,
      createdAt: project?.created_at,
      updatedAt: project?.updated_at
    }));
  },

  async getProjectsByClient(clientId) {
    const { data, error } = await supabase?.from('projects')?.select(`
        *,
        creator:user_profiles!projects_created_by_fkey(full_name)
      `)?.eq('client_id', clientId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(project => ({
      id: project?.id,
      name: project?.name,
      description: project?.description,
      startDate: project?.start_date,
      estimatedEndDate: project?.estimated_end_date,
      status: project?.status,
      creatorName: project?.creator?.full_name
    }));
  },

  async getProjectById(projectId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    const { data, error } = await supabase?.from('projects')?.select(`
        *,
        client:user_profiles!projects_client_id_fkey(id, email, full_name, role),
        creator:user_profiles!projects_created_by_fkey(id, full_name)
      `)?.eq('id', projectId)?.single();
    
    if (error) throw error;

    // Get current user profile to check access
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    
    // If user is cliente, verify they have access to this project
    if (profile?.role === 'cliente' && data?.client_id !== user?.id) {
      throw new Error('Você não tem permissão para acessar este projeto');
    }
    
    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      clientId: data?.client_id,
      clientName: data?.client?.full_name,
      clientEmail: data?.client?.email,
      createdBy: data?.created_by,
      creatorName: data?.creator?.full_name,
      startDate: data?.start_date,
      estimatedEndDate: data?.estimated_end_date,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createProject(projectData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    // Verify user is admin
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    if (profile?.role !== 'admin') {
      throw new Error('Apenas administradores podem criar projetos');
    }

    const { data, error } = await supabase?.from('projects')?.insert({
        name: projectData?.name,
        description: projectData?.description,
        client_id: projectData?.clientId,
        created_by: user?.id,
        start_date: projectData?.startDate,
        estimated_end_date: projectData?.estimatedEndDate,
        status: projectData?.status || 'ativo'
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async updateProject(projectId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    // Verify user is admin
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    if (profile?.role !== 'admin') {
      throw new Error('Apenas administradores podem editar projetos');
    }

    const dbUpdates = {};
    if (updates?.name !== undefined) dbUpdates.name = updates?.name;
    if (updates?.description !== undefined) dbUpdates.description = updates?.description;
    if (updates?.clientId !== undefined) dbUpdates.client_id = updates?.clientId;
    if (updates?.startDate !== undefined) dbUpdates.start_date = updates?.startDate;
    if (updates?.estimatedEndDate !== undefined) dbUpdates.estimated_end_date = updates?.estimatedEndDate;
    if (updates?.status !== undefined) dbUpdates.status = updates?.status;

    const { data, error } = await supabase?.from('projects')?.update(dbUpdates)?.eq('id', projectId)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async deleteProject(projectId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Não autenticado');

    // Verify user is admin
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    if (profile?.role !== 'admin') {
      throw new Error('Apenas administradores podem excluir projetos');
    }

    const { error } = await supabase?.from('projects')?.delete()?.eq('id', projectId);
    
    if (error) throw error;
  }
};